import { Injectable } from "@angular/core";
import { LaunchDarklyAdapter } from './launch-darkly.adapter';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import { AppConfigService } from "./app-config.service";
import { UserService } from "./user.service";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { LaunchDarklyActions } from "@core/store/actions";
import { LaunchDarklyState, initialState } from "@entities/launch-darkly/launch-darkly.reducer";
import { Store } from "@ngrx/store";
import { AgencyService } from "./agency.service";

export type LaunchDarklyServiceValue = string | number | boolean;

export interface LaunchDarklyServiceSubscriber {
  id: number;
  key: string;
  subject: Subject<LaunchDarklyServiceValue>;
}

@Injectable({
  providedIn: 'root',
})
export class LaunchDarklyService {
  private client: LaunchDarkly.LDClient;
  private state: LaunchDarklyState = {...initialState};
  private subscribers: LaunchDarklyServiceSubscriber[] = [];
  private nextId = 1;
  private context: any = {
    kind: 'user',
    key: 'user',
    role: '', // "NSS" | "IA"
    agencyCode: '',
    userId: '',
  };

  constructor(
    private launchDarklyAdapter: LaunchDarklyAdapter,
    private appConfigService: AppConfigService,
    private userService: UserService,
    private agencyService: AgencyService,
    private store: Store
  ) {
    const options: LaunchDarkly.LDOptions = {};
    this.client = this.launchDarklyAdapter.initialize(
      this.appConfigService.config.launchDarklyEnvKey,
      this.context,
      options
    );
    this.client.on("change", e => {
      this.receiveState(e);
    });
    this.acquireAndDeliverContext();
  }

  /**
   * Reports changes to this field as they happen.
   * Guaranteed to emit one value synchronously.
   */
  listen(key: string): Observable<LaunchDarklyServiceValue> {
    const id = this.nextId++;
    const subject = new BehaviorSubject<LaunchDarklyServiceValue>((this.state as any)[key] || '');
    const superSubscribe = subject.subscribe.bind(subject);
    (subject as any).subscribe = (observer: any) => {
      const subscriber = superSubscribe(observer);
      const superUnsubscribe = subscriber.unsubscribe.bind(subscriber);
      subscriber.unsubscribe = () => {
        superUnsubscribe();
        const p = this.subscribers.findIndex(s => s.subject === subject);
        if (p >= 0) {
          this.subscribers.splice(p, 1);
        }
      };
    };
    this.subscribers.push({ id, subject, key });
    return subject.asObservable();
  }

  /**
   * Try not to need this. acquireAndDeliverContext() is a neater way to gather the criteria, if they're all store-resident.
   */
  setCriterion(key: string, value: string): void {
    if (this.context[key] === value) {
      return;
    }
    this.context[key] = value;
    this.client.identify(this.context);
  }

  /**
   * My hope is that we'll be able to read all the necessary criteria from the Store, right here.
   * If criteria prove more complex than that, one can provide criteria piecemeal via setCriterion().
   */
  private acquireAndDeliverContext(): void {
    combineLatest([
      this.userService.getUser(),
      this.agencyService.getAgencyCode(),
    ]).subscribe(([
      user,
      agencyCode,
    ]) => {
      // Beware LD string matching is case-sensitive. In general, force lowercase.
      const context = {
        ...this.context,
        role: user?.role === 'NSS' ? 'NSS' : 'IA',
        userId: user?.userId?.toLowerCase() || '',
        agencyCode,
      };
      if (!this.contextsEquivalent(context, this.context)) {
        this.context = context;
        this.client.identify(this.context);
      }
    });
  }

  private contextsEquivalent(a: any, b: any): boolean {
    return (
      a.role === b.role &&
      a.agencyCode === b.agencyCode &&
      a.userId === b.userId
    );
  }

  private receiveState(state: LaunchDarkly.LDFlagChangeset): void {
    let changed = false;
    for (const key of Object.keys(state) as string[]) {
      if (this.receiveField(key, state[key].current)) {
        changed = true;
      }
    }
    if (changed) {
      this.store.dispatch(
        LaunchDarklyActions.replaceState({ state: { ...this.state } })
      );
    }
  }

  private receiveField(key: string, value: any): boolean {
    if (
      (typeof(value) === 'string') ||
      (typeof(value) === 'number') ||
      (typeof(value) === 'boolean')
    ) {
      if (value !== (this.state as any)[key]) {
        (this.state as any)[key] = value;
        this.broadcast(key, value);
        return true;
      }
    }
    return false;
  }

  private broadcast(key: string, value: LaunchDarklyServiceValue): void {
    for (const subscriber of this.subscribers) {
      if (subscriber.key === key) {
        subscriber.subject.next(value);
      }
    }
  }
}
